import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, FileText, Mail, TrendingUp, GraduationCap, Copy, RefreshCw, Send } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { notify } from "@/lib/notify";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


type StudentRow = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  grade: string | null;
  section: string | null;
  assessment_status: string;
  parent_status: string;
  last_activity: string | null;
};

export default function Teacher() {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: mem } = await (supabase as any)
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .eq("member_role", "teacher")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();

      const org = mem?.organization_id as string | undefined;
      if (!org) {
        setLoading(false);
        return;
      }
      setOrgId(org);

      const { data: assignments } = await (supabase as any)
        .from("teacher_student_assignments")
        .select("student_user_id")
        .eq("teacher_user_id", user.id)
        .eq("organization_id", org);

      const studentIds: string[] = (assignments ?? []).map(
        (a: any) => a.student_user_id,
      );
      if (studentIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const [{ data: members }, { data: profiles }, { data: assessments }, { data: parents }] =
        await Promise.all([
          (supabase as any)
            .from("organization_members")
            .select("user_id, grade, section, updated_at")
            .in("user_id", studentIds)
            .eq("organization_id", org),
          supabase
            .from("profiles")
            .select("id, full_name, email")
            .in("id", studentIds),
          supabase
            .from("assessments")
            .select("user_id, status, updated_at")
            .in("user_id", studentIds),
          (supabase as any)
            .from("parent_child_links")
            .select("student_user_id, verified_at")
            .in("student_user_id", studentIds),
        ]);

      const memberMap = new Map((members ?? []).map((m: any) => [m.user_id, m]));
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));
      const assessMap = new Map((assessments ?? []).map((a: any) => [a.user_id, a]));
      const parentMap = new Map(
        (parents ?? []).map((p: any) => [p.student_user_id, p]),
      );

      setRows(
        studentIds.map((id) => {
          const p = profileMap.get(id) as any;
          const m = memberMap.get(id) as any;
          const a = assessMap.get(id) as any;
          const pl = parentMap.get(id) as any;
          return {
            user_id: id,
            full_name: p?.full_name ?? null,
            email: p?.email ?? null,
            grade: m?.grade ?? null,
            section: m?.section ?? null,
            assessment_status: a?.status ?? "not_started",
            parent_status: pl?.verified_at ? "connected" : pl ? "invited" : "none",
            last_activity: a?.updated_at ?? m?.updated_at ?? null,
          };
        }),
      );
      setLoading(false);
    })();
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.full_name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.grade?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const completed = rows.filter((r) =>
      ["completed", "scored"].includes(r.assessment_status),
    ).length;
    const pending = total - completed;
    const parentsConnected = rows.filter((r) => r.parent_status === "connected").length;
    const parentsInvited = rows.filter((r) => r.parent_status !== "none").length;
    return {
      total,
      completed,
      pending,
      parentsInvited,
      parentEngagement: total ? Math.round((parentsConnected / total) * 100) : 0,
    };
  }, [rows]);

  const inviteLink = (token: string) =>
    `${window.location.origin}/parent/accept?token=${token}`;

  const notifyOrgAdmins = async (action: string, details: Record<string, unknown>) => {
    if (!orgId) return;
    const { data: admins } = await (supabase as any)
      .from("organization_members")
      .select("user_id")
      .eq("organization_id", orgId)
      .eq("member_role", "institution_admin")
      .eq("status", "active");
    const ids = (admins ?? []).map((a: any) => a.user_id);
    if (ids.length === 0) return;
    await notify({
      userIds: ids,
      type: "parent_invite_sent",
      title: action === "resent" ? "Parent invitation resent" : "Parent invitation sent",
      body: `${details.studentName ?? "A learner"} → ${details.email}`,
      organizationId: orgId,
      data: details,
    });
  };

  const findLatestInvite = async (student: StudentRow) => {
    if (!orgId || !student.email) return null;
    const { data } = await (supabase as any)
      .from("org_invites")
      .select("*")
      .eq("organization_id", orgId)
      .eq("invite_role", "parent")
      .eq("student_email", student.email)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return data;
  };

  const inviteParent = async (student: StudentRow) => {
    if (!orgId || !user) return;
    const email = prompt(`Parent email for ${student.full_name ?? "student"}?`);
    if (!email) return;
    await createInvite(student, email.trim().toLowerCase());
  };

  const createInvite = async (student: StudentRow, email: string, reason: "new" | "resent" | "regenerated" = "new") => {
    if (!orgId || !user) return;
    // Server-side guarded: only active teachers / institution admins of this
    // school may create an invite, and the token is generated on the server.
    const { data: invite, error } = await (supabase as any).rpc("create_org_invite", {
      _org: orgId,
      _email: email,
      _invite_role: "parent",
      _student_email: student.email,
      _expires_in_days: 14,
    });
    if (error) {
      toast({ title: "Invite failed", description: error.message, variant: "destructive" });
      return;
    }
    const token: string = invite?.token;

    await (supabase as any).from("institution_audit_log").insert({
      organization_id: orgId,
      action: reason === "resent" ? "parent_invite_resent" : reason === "regenerated" ? "parent_invite_regenerated" : "parent_invite_sent",
      student_user_id: student.user_id,
      performed_by: user.id,
      details: { email, token },
    });
    await notifyOrgAdmins(reason, { email, studentName: student.full_name, token });
    try {
      await navigator.clipboard.writeText(inviteLink(token));
    } catch { /* ignore */ }
    toast({
      title: reason === "new" ? "Invite created" : reason === "resent" ? "Invitation resent" : "New link generated",
      description: `Link copied to clipboard · ${inviteLink(token)}`,
    });
  };

  const copyInviteLink = async (student: StudentRow) => {
    const invite = await findLatestInvite(student);
    if (!invite) {
      toast({ title: "No invite yet", description: "Send a parent invitation first.", variant: "destructive" });
      return;
    }
    const expired = new Date(invite.expires_at) < new Date();
    if (expired) {
      toast({
        title: "This invitation has expired",
        description: "Generate a new link to share with the parent.",
        variant: "destructive",
      });
      return;
    }
    await navigator.clipboard.writeText(inviteLink(invite.token));
    toast({ title: "Link copied", description: "Share it with the parent." });
  };

  const resendInvite = async (student: StudentRow) => {
    const invite = await findLatestInvite(student);
    if (!invite) return inviteParent(student);
    await createInvite(student, invite.email, "resent");
  };

  const regenerateInvite = async (student: StudentRow) => {
    const invite = await findLatestInvite(student);
    if (!invite) return inviteParent(student);
    await createInvite(student, invite.email, "regenerated");
  };



  if (loading) return <div className="container py-10 text-muted-foreground">Loading…</div>;
  if (!orgId)
    return (
      <div className="container max-w-2xl py-16 text-center">
        <h1 className="text-3xl font-bold mb-3">No teaching assignment</h1>
        <p className="text-muted-foreground mb-6">
          Your school admin hasn't linked you to a cohort yet.
        </p>
        <Link to="/dashboard"><Button>Go to Dashboard</Button></Link>
      </div>
    );

  return (
    <>
      <PageHead title="Teacher Dashboard | WeKIT™" description="Track assigned learners" path="/teacher" />
      <div className="container max-w-6xl py-10 space-y-8">
        <header>
          <h1 className="text-3xl font-bold">Teacher Dashboard</h1>
          <p className="text-muted-foreground mt-1">Assigned learners and their journey.</p>
        </header>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={<GraduationCap className="w-4 h-4" />} label="Assigned" value={kpis.total} />
          <Kpi icon={<FileText className="w-4 h-4" />} label="Completed" value={kpis.completed} />
          <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Pending" value={kpis.pending} />
          <Kpi icon={<Users className="w-4 h-4" />} label="Parent engagement" value={`${kpis.parentEngagement}%`} />
        </section>

        <Card>
          <CardHeader className="flex-row items-center justify-between gap-4">
            <CardTitle>Learner roster</CardTitle>
            <Input
              placeholder="Search by name, email, grade…"
              className="max-w-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-muted-foreground text-sm">No learners match.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Assessment</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.user_id}>
                      <TableCell>
                        <div className="font-medium">{r.full_name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{r.email}</div>
                      </TableCell>
                      <TableCell>{[r.grade, r.section].filter(Boolean).join(" / ") || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={["completed", "scored"].includes(r.assessment_status) ? "default" : "secondary"}>
                          {r.assessment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={r.parent_status === "connected" ? "default" : "outline"}>
                          {r.parent_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Mail className="w-3 h-3 mr-1" /> Parent
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {r.parent_status === "none" ? (
                              <DropdownMenuItem onClick={() => inviteParent(r)}>
                                <Send className="w-3.5 h-3.5 mr-2" /> Invite parent
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onClick={() => copyInviteLink(r)}>
                                  <Copy className="w-3.5 h-3.5 mr-2" /> Copy invitation link
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => resendInvite(r)}>
                                  <Send className="w-3.5 h-3.5 mr-2" /> Resend invitation
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => regenerateInvite(r)}>
                                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Generate new link
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">{icon}{label}</div>
        <div className="text-3xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
